<template>
    <v-form ref="form" v-model="valid" lazy-validation>
        <v-card class="overflow-x-hidden raffle-dialog-card">

            <!-- ── Header ── -->
            <div class="raffle-dialog-header">
                <div class="raffle-dialog-header-content">
                    <v-icon color="white" class="mr-2">mdi-trophy-outline</v-icon>
                    <span class="raffle-dialog-title">Raffle Prize Details</span>
                    <v-chip v-if="form.major_prize" small color="amber darken-1" text-color="black" class="ml-3 font-weight-bold major-prize-chip">
                        <v-icon small left>mdi-star</v-icon> Grand Prize
                    </v-chip>
                </div>
                <v-btn icon small @click="closeDialog()" class="raffle-close-btn">
                    <v-icon color="rgba(255,255,255,0.8)">mdi-close</v-icon>
                </v-btn>
            </div>

            <v-card-text class="raffle-dialog-body pa-0">

                <!-- ── Image Upload ── -->
                <div class="image-upload-section">
                    <v-hover v-model="isHover">
                        <template v-slot="{ hover }">
                            <div class="image-upload-wrapper" :class="{ 'is-hovered': hover }">
                                <v-img
                                    :src="form.prize_image ? form.prize_image : checkImagePath(form.image_path)"
                                    contain
                                    height="160"
                                    class="image-preview"
                                ></v-img>

                                <!-- Upload overlay (edit/submit mode only) -->
                                <v-fade-transition>
                                    <div v-if="hover && !disabled_view" class="image-upload-overlay">
                                        <label for="upload-file" class="image-upload-label">
                                            <v-icon size="36" color="white">mdi-tray-arrow-up</v-icon>
                                            <span class="upload-hint">Upload Image</span>
                                        </label>
                                    </div>
                                </v-fade-transition>

                                <!-- Fullscreen button (always visible on hover) -->
                                <v-fade-transition>
                                    <v-btn
                                        v-if="hover"
                                        icon
                                        small
                                        class="image-fullscreen-btn"
                                        @click.stop="imagePreviewOpen = true"
                                    >
                                        <v-icon color="white" size="18">mdi-fullscreen</v-icon>
                                    </v-btn>
                                </v-fade-transition>
                            </div>
                        </template>
                    </v-hover>
                    <input class="d-none" id="upload-file" type="file" ref="fileInput" @change="uploadImage" accept="image/png, image/jpeg"/>
                </div>

                <!-- ── Fullscreen Image Lightbox ── -->
                <v-dialog
                    v-model="imagePreviewOpen"
                    fullscreen
                    :retain-focus="false"
                    content-class="lightbox-fullscreen-dialog"
                    transition="fade-transition"
                >
                    <div class="lightbox-fs-container" @click="imagePreviewOpen = false">
                        <!-- Close button -->
                        <v-btn icon class="lightbox-fs-close" @click.stop="imagePreviewOpen = false">
                            <v-icon color="white" size="26">mdi-close-circle</v-icon>
                        </v-btn>

                        <!-- Prize name badge -->
                        <div v-if="form.name" class="lightbox-fs-badge">
                            <v-icon small color="rgba(255,255,255,0.7)" class="mr-1">mdi-trophy-outline</v-icon>
                            {{ form.name }}
                        </div>

                        <!-- Image -->
                        <div class="lightbox-fs-img-wrapper" @click.stop>
                            <v-img
                                :src="form.prize_image ? form.prize_image : checkImagePath(form.image_path)"
                                contain
                                max-height="78vh"
                                max-width="80vw"
                                class="lightbox-fs-img"
                            ></v-img>
                        </div>

                        <!-- Hint -->
                        <div class="lightbox-fs-hint">
                            <v-icon size="14" color="rgba(255,255,255,0.3)" class="mr-1">mdi-cursor-default-click-outline</v-icon>
                            Click anywhere outside the image to close
                        </div>
                    </div>
                </v-dialog>

                <div class="form-sections px-5 pb-4">

                    <!-- ── Section: Basic Info ── -->
                    <div class="form-section-label">
                        <v-icon small color="#7eb3ff" class="mr-1">mdi-information-outline</v-icon>
                        Basic Information
                    </div>
                    <v-row no-gutters class="mb-1">
                        <v-col cols="4" class="pr-2">
                            <v-text-field
                                v-model="form.code"
                                :disabled="disabled_view"
                                label="Code"
                                :rules="errors.required"
                                outlined
                                dense
                                dark
                                color="#7eb3ff"
                                class="raffle-field"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="8">
                            <v-text-field
                                v-model="form.name"
                                :rules="errors.required"
                                :disabled="disabled_view"
                                label="Prize Name"
                                outlined
                                dense
                                dark
                                color="#7eb3ff"
                                class="raffle-field"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <!-- ── Section: Draw Settings ── -->
                    <div class="form-section-label">
                        <v-icon small color="#7eb3ff" class="mr-1">mdi-cog-outline</v-icon>
                        Draw Settings
                    </div>
                    <v-row no-gutters class="mb-1">
                        <v-col cols="4" class="pr-2">
                            <v-text-field
                                v-model="form.winner_count"
                                :rules="errors.required"
                                :disabled="disabled_view"
                                label="Winner Count"
                                type="number"
                                min="0"
                                hide-spin-buttons
                                outlined
                                dense
                                dark
                                color="#7eb3ff"
                                class="raffle-field"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="4" class="px-1">
                            <v-text-field
                                v-model="form.order"
                                label="Order"
                                type="number"
                                min="0"
                                :disabled="disabled_view"
                                readonly
                                outlined
                                :rules="errors.required"
                                dense
                                dark
                                color="#7eb3ff"
                                class="raffle-field"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="4" class="pl-2">
                            <v-text-field
                                v-model="form.batch"
                                :disabled="disabled_view"
                                label="Batch"
                                type="number"
                                min="1"
                                hide-spin-buttons
                                outlined
                                dense
                                dark
                                color="#7eb3ff"
                                class="raffle-field"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <!-- ── Section: Group & Category ── -->
                    <div class="form-section-label">
                        <v-icon small color="#7eb3ff" class="mr-1">mdi-tag-multiple-outline</v-icon>
                        Group & Eligibility
                    </div>
                    <v-row no-gutters class="mb-1">
                        <v-col cols="12" class="mb-1">
                            <v-autocomplete
                                v-model="form.raffle_group_id"
                                :rules="errors.required"
                                :items="GET_RAFFLE_GROUP_SELECTION"
                                :disabled="disabled_view"
                                label="Raffle Group"
                                item-text="name"
                                item-value="id"
                                auto-select-first
                                clearable
                                outlined
                                dense
                                dark
                                color="#7eb3ff"
                                class="raffle-field"
                            ></v-autocomplete>
                        </v-col>
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="form.tenure_categories"
                                :rules="errors.required"
                                :items="dropdowns.tenure_categories"
                                :disabled="disabled_view"
                                label="Tenure Categories"
                                item-text="text"
                                item-value="value"
                                auto-select-first
                                deletable-chips
                                small-chips
                                clearable
                                multiple
                                outlined
                                dense
                                dark
                                color="#7eb3ff"
                                class="raffle-field"
                            ></v-autocomplete>
                        </v-col>
                    </v-row>

                    <!-- ── Section: Flags ── -->
                    <div class="form-section-label">
                        <v-icon small color="#7eb3ff" class="mr-1">mdi-flag-outline</v-icon>
                        Flags
                    </div>
                    <div class="flags-row mb-3">
                        <div class="flag-item" :class="{ 'flag-active': form.suspense_animate }">
                            <v-checkbox
                                v-model="form.suspense_animate"
                                :true-value="1"
                                :false-value="0"
                                :disabled="disabled_view"
                                color="#7eb3ff"
                                hide-details
                                dense
                                class="mt-0 pt-0"
                            >
                                <template v-slot:label>
                                    <div class="flag-label">
                                        <v-icon small :color="form.suspense_animate ? '#7eb3ff' : 'grey'" class="mr-1">mdi-animation-play-outline</v-icon>
                                        Suspense Animation
                                    </div>
                                </template>
                            </v-checkbox>
                        </div>
                        <div class="flag-item" :class="{ 'flag-active major-prize-active': form.major_prize }">
                            <v-checkbox
                                v-model="form.major_prize"
                                :true-value="1"
                                :false-value="0"
                                :disabled="disabled_view"
                                color="amber darken-1"
                                hide-details
                                dense
                                class="mt-0 pt-0"
                            >
                                <template v-slot:label>
                                    <div class="flag-label">
                                        <v-icon small :color="form.major_prize ? 'amber darken-1' : 'grey'" class="mr-1">mdi-star-outline</v-icon>
                                        Grand Prize
                                    </div>
                                </template>
                            </v-checkbox>
                        </div>
                    </div>

                    <!-- ── Section: Winner Info (Update/View only) ── -->
                    <template v-if="ACTION !== 'Submit'">
                        <div class="form-section-label">
                            <v-icon small color="#7eb3ff" class="mr-1">mdi-trophy-variant-outline</v-icon>
                            Winner Information
                        </div>

                        <!-- Company mode -->
                        <template v-if="raffleSettings.data_grouping == 2">
                            <template v-if="form.company_name">
                                <v-autocomplete
                                    v-model="form.ticket_num"
                                    :items="dropdowns.company_tickets"
                                    :rules="errors.required"
                                    :disabled="disabled_view"
                                    item-text="value"
                                    label="Winner Ticket #"
                                    clearable
                                    outlined
                                    dense
                                    dark
                                    color="#7eb3ff"
                                    class="raffle-field mb-1"
                                    @input="getCompanyNameOfTicket()"
                                    :item-disabled="checkItemDisabled"
                                ></v-autocomplete>
                                <v-text-field
                                    v-model="form.company_name"
                                    :disabled="disabled_view"
                                    label="Winner Company Name"
                                    :rules="errors.required"
                                    readonly
                                    outlined
                                    dense
                                    dark
                                    color="#7eb3ff"
                                    class="raffle-field"
                                ></v-text-field>
                            </template>
                            <template v-else>
                                <v-container class="text-center pa-6">
                                    <v-icon large color="grey lighten-1">mdi-trophy-outline</v-icon>
                                    <div class="mt-3 grey--text text-subtitle-1">No winners found yet</div>
                                </v-container>
                            </template>
                        </template>

                        <!-- Guest mode -->
                        <template v-if="raffleSettings.data_grouping == 1">
                            <template v-if="form.guest_name">
                                <v-text-field
                                    v-model="form.employee_id"
                                    :disabled="disabled_view"
                                    label="Employee ID"
                                    readonly
                                    :rules="errors.required"
                                    outlined
                                    dense
                                    dark
                                    color="#7eb3ff"
                                    class="raffle-field mb-1"
                                ></v-text-field>
                                <v-text-field
                                    v-model="form.guest_name"
                                    :disabled="disabled_view"
                                    label="Guest Name"
                                    readonly
                                    :rules="errors.required"
                                    outlined
                                    dense
                                    dark
                                    color="#7eb3ff"
                                    class="raffle-field"
                                ></v-text-field>
                            </template>
                            <template v-else>
                                <v-container class="text-center pa-6">
                                    <v-icon large color="grey lighten-1">mdi-trophy-outline</v-icon>
                                    <div class="mt-3 grey--text text-subtitle-1">No winners found yet</div>
                                </v-container>
                            </template>
                        </template>
                    </template>

                </div>
            </v-card-text>

            <!-- ── Actions ── -->
            <div class="raffle-dialog-actions">
                <template v-if="actions !== 'View'">
                    <v-btn
                        @click="actions == 'Submit' ? addRafflePrize() : updateRafflePrize()"
                        :disabled="disabled_view"
                        depressed
                        class="raffle-submit-btn"
                    >
                        <v-icon left small>{{ actions === 'Submit' ? 'mdi-plus' : 'mdi-content-save-outline' }}</v-icon>
                        {{ actions }}
                    </v-btn>
                </template>

                <template v-if="actions === 'View'">
                    <v-btn
                        color="#1565c0"
                        depressed
                        :disabled="!hasVoidedHistory"
                        @click="viewVoidedHistory"
                        class="raffle-void-history-btn"
                    >
                        <v-icon left small>mdi-history</v-icon>
                        Voided History
                    </v-btn>
                    <v-btn
                        color="#c62828"
                        depressed
                        v-show="!isVoidButtonDisabled"
                        @click="voidRafflePrize"
                        class="raffle-void-btn"
                    >
                        <v-icon left small>mdi-cancel</v-icon>
                        Void
                    </v-btn>
                </template>
            </div>

            <!-- ── Voided History Dialog ── -->
            <v-dialog v-model="dialog" persistent max-width="800px" :retain-focus="false">
                <v-card class="voided-history-card">
                    <div class="voided-history-header">
                        <v-icon color="white" small class="mr-2">mdi-history</v-icon>
                        <span>Voided History</span>
                        <v-spacer></v-spacer>
                        <v-btn icon small @click="dialog = false">
                            <v-icon color="rgba(255,255,255,0.8)" small>mdi-close</v-icon>
                        </v-btn>
                    </div>
                    <v-card-text class="pa-4">
                        <v-data-table
                            v-if="ACTION !== 'Submit' && raffleSettings.data_grouping == 1"
                            :headers="guestHeaders"
                            :items="voidedHistory"
                            item-key="winner_id"
                            class="elevation-0"
                            :items-per-page="voidedHistory.length"
                            hide-default-footer
                            dense
                            :loading="loadingGuestHistory"
                            loading-text="Loading guest history..."
                        >
                            <template v-slot:item="props">
                                <tr>
                                    <td>{{ props.item.guest_name }}</td>
                                    <td>{{ props.item.type }}</td>
                                    <td>{{ props.item.time_voided }}</td>
                                </tr>
                            </template>
                        </v-data-table>
                        <span v-if="!loadingGuestHistory && ACTION !== 'Submit' && raffleSettings.data_grouping == 1 && !voidedHistory.length" class="text--secondary">
                            No guest data available
                        </span>
                        <v-data-table
                            v-if="ACTION !== 'Submit' && raffleSettings.data_grouping == 2"
                            :headers="companyHeaders"
                            :items="companyHistory"
                            item-key="ticket_num"
                            class="elevation-0"
                            :items-per-page="companyHistory.length"
                            hide-default-footer
                            dense
                            :loading="loadingCompanyHistory"
                            loading-text="Loading company history..."
                        >
                            <template v-slot:item="props">
                                <tr>
                                    <td>{{ props.item.company_name }}</td>
                                    <td>{{ props.item.ticket_num }}</td>
                                    <td>{{ props.item.type }}</td>
                                    <td>{{ props.item.time_voided }}</td>
                                </tr>
                            </template>
                        </v-data-table>
                        <span v-if="!loadingCompanyHistory && ACTION !== 'Submit' && raffleSettings.data_grouping == 2 && !companyHistory.length" class="text--secondary">
                            No company data available
                        </span>
                    </v-card-text>
                </v-card>
            </v-dialog>

        </v-card>
    </v-form>
</template>

<script>
import Swal from "sweetalert2";
import { mapGetters } from "vuex";
import SharedFunctionsComponent from "@/components/shared/SharedFunctionsComponent.vue";
export default {
    mixin: [SharedFunctionsComponent],
    data() {
        return {
            baseUrlAPI: {
                get: "rafflePrizes"
            },
            voidedHistory: [],
            companyHistory: [],
            dialog: false,
            hasVoidedHistory: false,
            loadingGuestHistory: false,
            loadingCompanyHistory: false,
            guestHeaders: [],
            companyHeaders: [],
            headers: [],
            valid: false,
            overlay: false,
            disabled_view: false,
            form: {},
            isHover: false,
            imagePreviewOpen: false,
            raffleSettings: {
                data_grouping: null
            },
            dropdown_function_name: [
                'getCompanyTickets',
                'getTenureCategories'
            ],
            dropdowns: {
                company_tickets: [],
                guest_lists: [],
                tenure_categories: []
            },
            company_id_winners: [],
            errors: {
                required: [v => !!v || 'The field is required.'],
                code: [v => !!v || 'The code is required'],
                name: [v => !!v || 'The name is required'],
                order: [v => !!v || 'The order is required'],
                winner_count: [v => !!v || 'The winner_count is required'],
                guest_name: [v => !!v || 'The guest_name is required'],
                employee_id: [v => !!v || 'The employee_id is required'],
                company_name: [v => !!v || 'The company_name is required'],
                ticket_num: [v => !!v || 'The ticket_num is required'],
                tenure_categories: [v => !!v || 'The tenure_categories is required'],
            },
            winner_ids: [],
        };
    },
    computed: {
        ...mapGetters([
            "ACTION",
            "DIALOGS",
            "GET_DIALOG_VIEW",
            "GET_EVENT_ID",
            "GET_RAFFLE_GROUP_SELECTION",
        ]),
        actions() {
            this.getDropdowns();
            if (this.ACTION == "Submit") {
                this.disabled_view = false;
                this.isViewBtn = false;
                this.getLatestOrderPrizes();
                return "Submit";
            } else if (this.ACTION == "Update") {
                this.disabled_view = false;
                this.isViewBtn = true;
                this.getRafflePrizeData();
                return "Update";
            } else if (this.ACTION == "View") {
                this.disabled_view = true;
                this.isViewBtn = true;
                this.getRafflePrizeData();
                return "View";
            }
        },
        isVoidButtonDisabled() {
            if (this.raffleSettings.data_grouping === 1) {
                return !this.form.guest_name;
            } else if (this.raffleSettings.data_grouping === 2) {
                return !this.form.company_name;
            }
            return true;
        }
    },
    created() {
        this.initialForm();
    },
    mounted() {
        this.getRaffleDurationSettings();
        this.checkVoidedHistory();
        this.getWinnersTicket();
    },
    methods: {
        initialForm() {
            this.form = {
                code              : '',
                name              : '',
                order             : '',
                prize_image       : null,
                image_path        : null,
                suspense_animate  : 0,
                major_prize       : 0,
                batch             : 1,
                raffle_group_id   : 1,
                tenure_categories : []
            };
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = null;
            }
        },
        closeDialog() {
            this.$store.commit("DIALOG", false);
            this.$store.commit("ACTION", null);
            this.winner_ids = [];
        },
        async addRafflePrize() {
            this.$refs.form.validate();
            let code_swal_text         = this.form.code ? '' : 'Code';
            let name_swal_text         = this.form.name ? '' : 'Name';
            let winner_count_swal_text = this.form.winner_count ? '' : 'Winner Count';
            let order_swal_text        = this.form.order ? '' : 'Order';
            let file_swal_text         = '';
            const file = this.$refs.fileInput.files[0];
            if (!file) {
                file_swal_text = 'Prize Image';
            } else if (!['image/png', 'image/jpeg'].includes(file.type)) {
                file_swal_text = 'Valid Image (PNG or JPEG)';
            } else if (file.size > 2 * 1024 * 1024) {
                file_swal_text = 'Image below 2 MB';
            }

            let array_text = [code_swal_text, name_swal_text, order_swal_text, winner_count_swal_text, file_swal_text];
            let filtered   = array_text.filter((el) => el).join(', ');

            if (this.form.code && this.form.name && this.form.order && this.form.winner_count && !file_swal_text) {
                let payload = {
                    url: "rafflePrizes",
                    ...this.form,
                    event_id: this.GET_EVENT_ID,
                };

                await this.$store.dispatch('urlPost', payload).then(async (response) => {
                    Swal.fire(response.icon.toUpperCase(), response.message, response.icon);
                    this.$store.commit('NEW_MASTER_DATA', true);

                    await this.getLatestOrderPrizes();
                    this.form.code             = '';
                    this.form.name             = '';
                    this.form.prize_image      = null;
                    this.form.image_path       = null;
                    this.form.suspense_animate = 0;
                    this.form.major_prize      = 0;
                    this.form.batch            = 1;
                    this.form.raffle_group_id  = 1;
                    this.form.tenure_categories = [];
                    this.form.winner_count     = '';
                    if (this.$refs.fileInput) {
                        this.$refs.fileInput.value = null;
                    }
                    this.$store.commit("DIALOG", false);
                });
            } else {
                Swal.fire({
                    text: `Fill Required Fields (${filtered})`,
                    icon: 'warning',
                    allowOutsideClick: false,
                });
            }
        },
        async updateRafflePrize() {
            let payload = {
                url: "rafflePrizes",
                id: this.form.id,
                ...this.form,
                event_id: this.GET_EVENT_ID
            };

            try {
                const response = await this.$store.dispatch("urlPut", payload);
                this.$store.commit('NEW_MASTER_DATA', true);
                this.closeDialog();
                const swalInstance = await Swal.fire({
                    title: response.icon.toUpperCase(),
                    text: response.message,
                    icon: response.icon,
                    showConfirmButton: true,
                    allowOutsideClick: false,
                    focusConfirm: true
                });

                if (swalInstance.isConfirmed) {
                    Swal.close();
                }
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    await Swal.fire({ title: 'Unauthenticated!', text: 'Please log in again.', icon: 'error', allowOutsideClick: false });
                    localStorage.removeItem("token");
                    this.$router.push('/');
                } else {
                    await Swal.fire({ title: 'Error!', text: 'Something went wrong. Please try again.', icon: 'error' });
                }
            }
        },
        async getRafflePrizeData() {
            this.getWinnersTicket();
            this.form.id                = this.GET_DIALOG_VIEW.id;
            this.form.code              = this.GET_DIALOG_VIEW.code;
            this.form.name              = this.GET_DIALOG_VIEW.name;
            this.form.winner_count      = this.GET_DIALOG_VIEW.winner_count;
            this.form.order             = this.GET_DIALOG_VIEW.order;
            this.form.image_path        = this.GET_DIALOG_VIEW.image_path;
            this.form.suspense_animate  = this.GET_DIALOG_VIEW.suspense_animate;
            this.form.major_prize       = this.GET_DIALOG_VIEW.major_prize ?? 0;
            this.form.tenure_categories = this.GET_DIALOG_VIEW.tenure_categories;
            this.form.batch             = this.GET_DIALOG_VIEW.batch;
            this.form.raffle_group_id   = this.GET_DIALOG_VIEW.raffle_group_id;
            if (!_.isEmpty(this.GET_DIALOG_VIEW.winner_details)) {
                if (this.raffleSettings.data_grouping == 1) {
                    this.form.employee_id = this.GET_DIALOG_VIEW.winner_details.reference_number;
                    this.form.guest_name  = this.GET_DIALOG_VIEW.winner_details.full_name;
                } else {
                    this.form.company_name = this.GET_DIALOG_VIEW.winner_details.name;
                    this.form.ticket_num   = this.GET_DIALOG_VIEW.winner_details.ticket_num;
                }
            }
            if (this.ACTION === 'Update' && !!(this.form.guest_name || this.form.company_name)) {
                this.disabled_view = true;
            }
            this.checkVoidedHistory();
        },
        async checkVoidedHistory() {
            try {
                const response = await this.$store.dispatch('urlGet', {
                    url: 'voidedHistory',
                    params: { event_id: this.GET_EVENT_ID },
                    prize_id: this.form.id,
                });
                this.hasVoidedHistory = response.icon === 'success' && response.data && response.data.length > 0;
            } catch (error) {
                console.error('Error fetching voided history:', error);
                this.hasVoidedHistory = false;
            }
        },
        async voidRafflePrize() {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: 'Do you want to void this raffle prize?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, void it!',
                cancelButtonText: 'Cancel',
                reverseButtons: true,
            });

            if (result.isConfirmed) {
                try {
                    let payload = {
                        url: 'rafflePrizes/void',
                        id: this.form.id,
                        event_id: this.GET_EVENT_ID,
                    };
                    const response = await this.$store.dispatch('urlPut', payload);
                    Swal.fire(response.icon.toUpperCase(), response.message, response.icon);
                    if (response.icon === 'success') {
                        this.closeDialog();
                        this.$store.commit('NEW_MASTER_DATA', true);
                        this.hasVoidedHistory = true;
                        this.getWinnersTicket();
                    }
                } catch (error) {
                    Swal.fire('Error', 'An error occurred while trying to void the prize', 'error');
                }
            }
        },
        async viewVoidedHistory() {
            this.dialog = true;
            this.loadingGuestHistory   = true;
            this.loadingCompanyHistory = true;
            try {
                const response = await this.$store.dispatch('urlGet', {
                    url: 'voidedHistory',
                    params: { event_id: this.GET_EVENT_ID, prize_id: this.form.id },
                });

                if (response.icon === 'success' && response.data) {
                    this.voidedHistory = response.data.filter(item => item.type === 'guest').map(winner => ({
                        guest_name  : winner.guest_name,
                        type        : winner.type,
                        time_voided : winner.time_voided,
                    }));
                    this.companyHistory = response.data.filter(item => item.type === 'company').map(winner => ({
                        company_name : winner.company_name,
                        ticket_num   : winner.ticket_num,
                        type         : winner.type,
                        time_voided  : winner.time_voided,
                    }));
                    if (this.voidedHistory.length > 0) {
                        const firstItem = this.voidedHistory[0];
                        this.guestHeaders = Object.keys(firstItem).map(key => ({
                            text: key.replace('_', ' ').toUpperCase(),
                            value: key,
                        }));
                    }
                    if (this.companyHistory.length > 0) {
                        const firstItem = this.companyHistory[0];
                        this.companyHeaders = Object.keys(firstItem).map(key => ({
                            text: key.replace('_', ' ').toUpperCase(),
                            value: key,
                        }));
                    }
                } else {
                    this.voidedHistory  = [];
                    this.companyHistory = [];
                }
            } catch (error) {
                console.error('Error fetching voided history:', error);
            } finally {
                this.loadingGuestHistory   = false;
                this.loadingCompanyHistory = false;
            }
        },
        uploadImage(event) {
            let that = this;
            let reader = new FileReader();
            reader.addEventListener('load', () => {
                that.form.prize_image = reader.result;
            });
            let file = event.target.files[0];
            reader.readAsDataURL(file);
        },
        getLatestOrderPrizes() {
            let payload = { url: 'getLatestOrderPrizes' };
            this.$store.dispatch('urlPost', payload).then((res) => {
                this.form.order = res;
            }).catch((error) => {
                if (error.response.status == 401) {
                    Swal.fire({ title: 'Unauthenticated!', text: '', icon: 'error', allowOutsideClick: false }).then((result) => {
                        if (result.isConfirmed) {
                            localStorage.removeItem("token");
                            window.location.reload();
                            this.$router.push('/');
                        }
                    });
                }
            });
        },
        async getRaffleDurationSettings() {
            let payload = { type: 'Expansions', url: 'moduleSettings' };
            await this.$store.dispatch("urlGet", payload).then((res) => {
                res.forEach(setting => {
                    Object.keys(this.raffleSettings).forEach(key => {
                        if (key == setting.parameter) {
                            this.raffleSettings[key] = parseFloat(setting.value);
                        }
                    });
                });
            });
        },
        getDropdowns() {
            this.dropdown_function_name.forEach(fn => { this[fn](); });
        },
        getCompanyTickets() {
            let payload = { url: 'getCompanyTickets' };
            this.$store.dispatch('urlPost', payload).then(res => {
                this.dropdowns.company_tickets = res.lists;
                this.company_id_winners = res.company_id_winners;
            });
        },
        getCompanyNameOfTicket() {
            let ticket_company_name = this.dropdowns.company_tickets.find(x => x.value == this.form.ticket_num)?.company_name;
            this.form.company_name = ticket_company_name;
            this.$forceUpdate();
        },
        checkExistingWinnerCompany(item) {
            return this.company_id_winners.includes(item.company_id);
        },
        getTenureCategories() {
            let payload = { url: 'getTenureCategories' };
            this.$store.dispatch('urlPost', payload).then(res => {
                this.dropdowns.tenure_categories = res;
            });
        },
        checkItemDisabled(item) {
            return (
                this.winner_ids.map(e => e.winner_id).includes(item.value) &&
                !this.winner_ids.map(r => r.prize_id).includes(this.form.id)
            );
        },
        async getWinnersTicket() {
            let payload = { url: 'getWinnersTicket' };
            await this.$store.dispatch('urlPost', payload).then(res => {
                this.winner_ids = res;
            });
        },
        checkImagePath(image_path) {
            return image_path == null
                ? process.env.VUE_APP_API_HOST + "/images/no-image.png"
                : process.env.VUE_APP_API_HOST + "/" + image_path;
        },
        hasWinner(){
            console.log(this.winner_ids);
            return this.winner_ids.length > 0;
        }
    },
    watch: {
        DIALOGS: {
            handler(val) {
                if (!val) {
                    this.initialForm();
                    this.$store.commit('DIALOG_VIEW', {});
                }
            },
        },
        ACTION: {
            handler(val) {
                if (val == 'Submit') {
                    this.getLatestOrderPrizes();
                }
            },
        },
    },
};
</script>

<style scoped>
/* ── Card ── */
.raffle-dialog-card {
    background: #1a1e2e !important;
    border-radius: 12px !important;
    overflow: hidden;
}

/* ── Header ── */
.raffle-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px 14px 20px;
    background: linear-gradient(135deg, #1565c0 0%, #0d47a1 60%, #1a237e 100%);
}
.raffle-dialog-header-content {
    display: flex;
    align-items: center;
}
.raffle-dialog-title {
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.4px;
}
.major-prize-chip {
    font-size: 0.7rem !important;
    height: 22px !important;
}
.raffle-close-btn {
    opacity: 0.85;
}
.raffle-close-btn:hover {
    opacity: 1;
}

/* ── Image Upload ── */
.image-upload-section {
    background: #141728;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
}
.image-upload-wrapper {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    border: 2px dashed rgba(126,179,255,0.25);
    transition: border-color 0.2s;
    cursor: pointer;
}
.image-upload-wrapper.is-hovered {
    border-color: rgba(126,179,255,0.6);
}
.image-preview {
    background: #0d1120;
}
.image-upload-overlay {
    position: absolute;
    inset: 0;
    background: rgba(13, 71, 161, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(2px);
}
.image-upload-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    cursor: pointer;
}
.upload-hint {
    color: #fff;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.5px;
}

/* Fullscreen expand button */
.image-fullscreen-btn {
    position: absolute !important;
    bottom: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.55) !important;
    backdrop-filter: blur(4px);
    border-radius: 6px !important;
    z-index: 5;
    transition: background 0.2s;
}
.image-fullscreen-btn:hover {
    background: rgba(21, 101, 192, 0.85) !important;
}

/* Fullscreen lightbox */
.lightbox-fs-container {
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    gap: 16px;
}
.lightbox-fs-close {
    position: absolute !important;
    top: 18px;
    right: 18px;
    background: rgba(255, 255, 255, 0.1) !important;
    backdrop-filter: blur(6px);
    transition: background 0.2s, transform 0.2s;
}
.lightbox-fs-close:hover {
    background: rgba(198, 40, 40, 0.75) !important;
    transform: scale(1.1);
}
.lightbox-fs-badge {
    position: absolute;
    top: 22px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 20px;
    padding: 4px 16px;
    font-size: 0.82rem;
    color: rgba(255,255,255,0.85);
    font-weight: 600;
    letter-spacing: 0.4px;
    display: flex;
    align-items: center;
    white-space: nowrap;
}
.lightbox-fs-img-wrapper {
    cursor: default;
    border-radius: 12px;
    overflow: hidden;
    box-shadow:
        0 24px 80px rgba(0, 0, 0, 0.8),
        0 0 0 1px rgba(255,255,255,0.08);
}
.lightbox-fs-img {
    display: block;
}
.lightbox-fs-hint {
    font-size: 0.74rem;
    color: rgba(255, 255, 255, 0.3);
    letter-spacing: 0.4px;
    display: flex;
    align-items: center;
}

/* ── Form body ── */
.raffle-dialog-body {
    background: #1a1e2e !important;
}
.form-sections {
    padding-top: 14px;
}

/* ── Section labels ── */
.form-section-label {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #7eb3ff;
    margin-bottom: 8px;
    margin-top: 4px;
    display: flex;
    align-items: center;
    opacity: 0.85;
}

/* ── Fields ── */
.raffle-field .v-input__slot {
    background: rgba(255,255,255,0.05) !important;
}
.raffle-field .v-input__slot fieldset {
    border-color: rgba(126,179,255,0.25) !important;
}
.raffle-field.v-input--is-focused .v-input__slot fieldset {
    border-color: #7eb3ff !important;
}
.raffle-field input,
.raffle-field .v-select__selection {
    color: #e8eaf6 !important;
    font-size: 0.9rem;
}
.raffle-field .v-label {
    color: rgba(200,210,255,0.5) !important;
    font-size: 0.88rem;
}
.raffle-field.v-input--is-focused .v-label {
    color: #7eb3ff !important;
}

/* ── Flags row ── */
.flags-row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}
.flag-item {
    flex: 1;
    min-width: 160px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 8px;
    padding: 10px 14px;
    background: rgba(255,255,255,0.04);
    transition: border-color 0.2s, background 0.2s;
}
.flag-item.flag-active {
    border-color: rgba(126,179,255,0.45);
    background: rgba(126,179,255,0.08);
}
.flag-item.major-prize-active {
    border-color: rgba(255,179,0,0.5) !important;
    background: rgba(255,179,0,0.07) !important;
}
.flag-label {
    display: flex;
    align-items: center;
    font-size: 0.85rem;
    color: rgba(220,230,255,0.85);
    font-weight: 500;
}

/* ── Actions ── */
.raffle-dialog-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 14px 20px;
    border-top: 1px solid rgba(255,255,255,0.08);
    background: #141728;
    flex-wrap: wrap;
}
.raffle-submit-btn {
    background: linear-gradient(135deg, #1565c0, #0d47a1) !important;
    color: #fff !important;
    border-radius: 8px !important;
    text-transform: none !important;
    font-weight: 600;
    letter-spacing: 0.3px;
    padding: 0 24px !important;
    min-width: 120px;
}
.raffle-void-history-btn,
.raffle-void-btn {
    border-radius: 8px !important;
    text-transform: none !important;
    font-weight: 600;
    letter-spacing: 0.3px;
    color: #fff !important;
}

/* ── Voided History dialog ── */
.voided-history-card {
    background: #1a1e2e !important;
}
.voided-history-header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background: linear-gradient(135deg, #1565c0, #0d47a1);
    color: #fff;
    font-size: 0.95rem;
    font-weight: 700;
}
</style>

<style>
/* Fullscreen dialog — must be global since Vuetify renders outside the component root */
.lightbox-fullscreen-dialog {
    background: rgba(5, 8, 20, 0.97) !important;
    box-shadow: none !important;
    backdrop-filter: blur(8px);
}
</style>
